import { API_BASE_URL, axiosInstance } from "src/api/config/axiosInstance";
import { fetchWithAuth } from "src/api/fetchWithAuth";
import { stripSseKeepaliveNoise } from "src/utils/sseKeepalive";
import { joinStreamTextFragments } from "src/utils/streamTextJoin";

export interface FormatDictationRequestDto {
  text: string;
}

export interface FormatDictationResponseDto {
  success?: boolean;
  issuccess?: boolean;
  isSuccess?: boolean;
  data?: string;
  statusCode?: number;
  message?: string;
}

export type MedicalHistoryScope = "All" | "LastN" | "Selected";

export interface PatientAnalyzeRequestDto {
  ptId: number;
  processDate?: string | null;
  medicalHistoryScope?: MedicalHistoryScope;
  lastVisitCount?: number;
  selectedPtIds?: number[];
}

export interface PatientAnalyzeResponseDto {
  success?: boolean;
  issuccess?: boolean;
  isSuccess?: boolean;
  statusCode?: number;
  message?: string;
  data?: {
    ptId?: number;
    text?: string;
  } | null;
}

export function buildNurseListPatientAnalyzeRequest(ptId: number): PatientAnalyzeRequestDto {
  return {
    ptId,
    medicalHistoryScope: "LastN",
    lastVisitCount: 1,
  };
}

export function getPatientAnalyzeResultText(res: PatientAnalyzeResponseDto | undefined | null): string {
  const raw = res?.data?.text;
  return typeof raw === "string" ? raw.trim() : "";
}

const parseSseEventPayload = (rawEvent: string) => {
  const dataLines = rawEvent
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim());

  if (!dataLines.length) {
    return "";
  }

  const payload = dataLines.join("\n");
  if (!payload || payload === "[DONE]") {
    return "";
  }

  try {
    const parsed = JSON.parse(payload);
    if (typeof parsed === "string") return parsed;
    if (typeof parsed?.text === "string") return parsed.text;
    if (typeof parsed?.data?.text === "string") return parsed.data.text;
    if (typeof parsed?.delta === "string") return parsed.delta;
    if (typeof parsed?.content === "string") return parsed.content;
    if (typeof parsed?.message === "string") return parsed.message;
  } catch {
    return payload;
  }

  return "";
};

export type PatientAnalyzeStreamOptions = {
  onDelta: (accumulatedText: string) => void;
  signal?: AbortSignal;
};

export async function patientAnalyzeStream(
  body: PatientAnalyzeRequestDto,
  options: PatientAnalyzeStreamOptions
): Promise<string> {
  const response = await fetchWithAuth(`${API_BASE_URL}/external/api/AiAssistant/PatientAnalyzeStream`, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal: options.signal,
  });

  if (!response.ok) {
    const errBody = await response.text().catch(() => "");
    throw new Error(errBody.trim() || `PatientAnalyzeStream failed: ${response.status}`);
  }

  if (!response.body) {
    throw new Error("PatientAnalyzeStream: empty body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let fullText = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    buffer = stripSseKeepaliveNoise(buffer);
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";

    for (const eventChunk of events) {
      const chunk = stripSseKeepaliveNoise(eventChunk).trim();
      if (!chunk) continue;
      const piece = chunk.includes("data:") ? parseSseEventPayload(chunk) : chunk;
      if (!piece) continue;
      fullText = joinStreamTextFragments(fullText, piece);
      options.onDelta(fullText);
    }
  }

  const tail = stripSseKeepaliveNoise(buffer).trim();
  if (tail) {
    const piece = tail.includes("data:") ? parseSseEventPayload(tail) : tail;
    if (piece) {
      fullText = joinStreamTextFragments(fullText, piece);
      options.onDelta(fullText);
    }
  }

  return fullText.trim();
}

export const aiAssistantService = {
  formatDictation: async <T = FormatDictationResponseDto>(data: FormatDictationRequestDto): Promise<T> => {
    const response = await axiosInstance.post<T>("/external/api/AiAssistant/FormatDictation", data);
    return response.data;
  },
};
